import { useState, useEffect, useMemo } from "react";

const STAGES = [
  { id: "read", label: "Reading your responses" },
  { id: "map", label: "Mapping decision patterns" },
  { id: "analyze", label: "Analyzing relationship dynamics" },
  { id: "identify", label: "Identifying identity architecture" },
  { id: "generate", label: "Generating your personalized report" },
];

const QUOTES = [
  { id: "q1", text: "Identity determines behavior. Behavior determines outcomes." },
  { id: "q2", text: "Alignment is refinement, not destruction." },
  { id: "q3", text: "Burnout is not overwork — it's a misaligned identity operating under pressure." },
  { id: "q4", text: "You can't think your way out of a pattern you haven't seen yet." },
  { id: "q5", text: "The version that earns approval may not be the version that feels alive." },
];

const stagesLen = STAGES.length;
const quotesLen = QUOTES.length;

function OrbAnimation() {
  return (
    <div className="relative w-32 h-32 mb-10" data-testid="loading-orbs">
      <div
        className="absolute top-1/2 left-1/2 w-3 h-3 rounded-full bg-[#F3B21A]"
        style={{ animation: "pulse-orb 2s ease-in-out infinite", transform: "translate(-50%, -50%)" }}
      />
      <div
        className="absolute top-1/2 left-1/2 w-16 h-16 rounded-full border border-[#F3B21A] opacity-20"
        style={{ animation: "pulse-ring 2s ease-in-out infinite", transform: "translate(-50%, -50%)" }}
      />
      <div
        className="absolute top-1/2 left-1/2 w-28 h-28 rounded-full border border-[#F3B21A] opacity-10"
        style={{ animation: "pulse-ring 2s ease-in-out infinite 0.4s", transform: "translate(-50%, -50%)" }}
      />
      <div
        className="absolute w-1.5 h-1.5 rounded-full bg-[#E66E6D]"
        style={{ animation: "orbit 3s linear infinite", top: "10%", left: "50%" }}
      />
      <div
        className="absolute w-1 h-1 rounded-full bg-[#F3B21A]"
        style={{ animation: "orbit 4s linear infinite reverse", top: "50%", left: "10%" }}
      />
    </div>
  );
}

export default function LoadingScreen() {
  const [stageIndex, setStageIndex] = useState(0);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => (prev >= 95 ? 95 : prev + 0.5));
    }, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setStageIndex((prev) => (prev >= stagesLen - 1 ? stagesLen - 1 : prev + 1));
    }, 3200);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setQuoteIndex((prev) => (prev + 1) % quotesLen);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const currentStage = useMemo(() => STAGES[stageIndex], [stageIndex]);
  const currentQuote = useMemo(() => QUOTES[quoteIndex], [quoteIndex]);

  return (
    <div
      data-testid="loading-screen"
      className="min-h-screen bg-[#054452] flex flex-col items-center justify-center px-6"
    >
      <OrbAnimation />

      <p
        key={currentStage.id}
        className="font-body text-sm text-[#F3B21A] tracking-wide uppercase mb-4 loading-text-rotate"
        data-testid="loading-stage"
      >
        {currentStage.label}
      </p>

      <div className="w-full max-w-[300px] h-[2px] bg-[#0C6474] mb-10 overflow-hidden">
        <div
          className="h-full bg-[#F3B21A] transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <p
        key={currentQuote.id}
        className="font-display text-base sm:text-lg font-light italic text-[#E66E6D] loading-text-rotate text-center max-w-[440px] px-4"
        data-testid="loading-quote"
      >
        "{currentQuote.text}"
      </p>

      <p className="font-body text-[10px] tracking-[0.25em] uppercase text-[#0C6474] mt-8">
        Identity Analyzer
      </p>
    </div>
  );
}
